<template>
	<div id="statistics-container">
		<div id="graph-wrapper">
			<GraphCard
				title="LOTAÇÃO"
				:goal="goals.capacity"
				v-model="statistics.capacity"
				type="capacity"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
				:reverse="true"
			/>
			<GraphCard
				title="AGENDA"
				:goal="goals.schedulings_volume"
				v-model="statistics.schedulings_volume"
				type="schedulings_volume"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
				graphType="circular"
			/>
			<GraphCard
				title="TEMPO DE ESPERA"
				:goal="goals.waiting_time"
				v-model="statistics.waiting_time"
				type="waiting_time"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
				graphType="circular"
				:reverse="true"
			/>
			<GraphCard
				title="ESPERA EM ATRASO"
				:goal="goals.waiting_capacity"
				v-model="statistics.waiting_capacity"
				type="waiting_capacity"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
				:reverse="true"
			/>
			<GraphCard
				title="FALTAS"
				:goal="goals.no_show"
				v-model="statistics.no_show"
				type="no_show"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
				graphType="circular"
				:reverse="true"
			/>
			<GraphCard
				title="PRODUÇÃO"
				:goal="goals.production"
				v-model="statistics.production"
				type="production"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
				graphType="circular"
			/>
			<GraphCard
				title="PACIENTES EM ATENDIMENTO"
				:goal="goals.in_attendance_capacity"
				v-model="statistics.in_attendance_capacity"
				type="in_attendance_capacity"
				:hasFilters="hasFilters"
				@change-goal="ChangeGoalHandler"
			/>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import GraphCard from './components/GraphCard.vue';
import Swal from 'sweetalert2';

export default {
	name: 'GraphContainer',
	components: {
		GraphCard	
	},
	props: {
		modalities: {
			type: Array,
			default: () => [],
			required: true
		},
		schedules: {
			type: Array,
			default: () => [],
			required: true
		}
	},
	data() {
		return {
			goals: {
				capacity: 0,
				schedulings_volume: 0,
				no_show: 0,
				waiting_capacity: 0,
				production: 0,
				waiting_time: 0,
				in_attendance_capacity: 0,
			},
			statistics: {
				capacity: 0,
				schedulings_volume: 0,
				no_show: 0,
				waiting_capacity: 0,
				production: 0,
				waiting_time: 0,
				in_attendance_capacity: 0,
			},
			requesting: false,
			interval: null,
		}
	},
	watch: {
		modalities: {
			handler() {
				this.GetStatistics();
			},
			immediate: true
		},
		schedules: {
			handler() {
				this.GetStatistics();
			},
			immediate: true
		}
	},
	computed: {
		...mapGetters('auth', [
			'requester_selected_id'
		]),
		hasFilters() {
			return !!this.modalities.length || !!this.schedules.length
		}
	},
	methods: {
		ChangeGoalHandler({ key, value }) {
			this.goals[key] = value

			this.SaveGoal(key, value)
		},
		GetGoals() {
			return window.api.call('post', '/get-goals', {
				organization_id: this.requester_selected_id
			})
				.then(({status, data}) => {
					if(status === 200) {
						this.goals = { ...data.goals }
					}
				})
		},
		SaveGoal(key, value) {
			return window.api.call('post', '/save-goals', {
				organization_id: this.requester_selected_id,
				key,
				value
			})
				.then(({status, data}) => {
					if(status === 200) {
						this.goals = { ...data.goals }

						return;
					}

					Swal.fire({
						icon: 'error',
						title: 'Erro!',
						text: 'Ocorreu um erro ao definir a nova meta!'
					})
				})
		},
		GetStatistics() {
			if(!this.hasFilters || this.requesting) return;

			this.requesting = true;

			return window.api.call('post', '/get-statistics', {
				organization_id: this.requester_selected_id,
				modalities: this.modalities,
				schedules: this.schedules
			})
				.then(({status, data}) => {
					if(status === 200) {

						for (const [key, value] of Object.entries(data.statistics)) {
							this.statistics[key] = value.current ?? 0
						}

						return;
					}

					Swal.fire({
						icon: 'error',
						title: 'Erro!',
						text: 'Ocorreu um erro ao buscar as estatísticas!'
					})
				})
				.finally(() => {
					this.requesting = false;
				})
		},
	},
	async beforeMount() {
		await this.GetGoals()
	},
	mounted() {
		this.interval = setInterval(() => {
			this.GetStatistics()
		}, 60000)
	},
	// eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
	beforeDestroy() {
		clearInterval(this.interval)
	}
}
</script>

<style lang="scss" scoped>
	#statistics-container {
		width: 100%;
		height: 100%;
		background-color: var(--graphBackgroundColor);

		#graph-wrapper {
			width: 100%;
			height: 100%;
			padding: 1rem;

			display: grid;
			gap: 1rem;
			grid-template-columns: 25% 25% 48.75%;
			grid-template-rows: 1fr 1fr 1fr;
			grid-template-areas: 
				"capacity capacity capacity"
				"schedulings_volume waiting_time waiting_capacity"
				"no_show production in_attendance_capacity";

			.capacity { grid-area: capacity; }
			.schedulings_volume { grid-area: schedulings_volume; }
			.waiting_time { grid-area: waiting_time; }
			.waiting_capacity { grid-area: waiting_capacity; }
			.no_show { grid-area: no_show; }
			.production { grid-area: production; }
			.in_attendance_capacity { grid-area: in_attendance_capacity; }
		}

		@media (max-width: 900px) {
			#graph-wrapper {
				grid-template-columns: 50% 50%;
				grid-template-rows: 1fr 1fr;
				grid-template-areas:
					"capacity capacity"
					"schedulings_volume waiting_time"
					"waiting_capacity waiting_capacity"
					"no_show production"
					"in_attendance_capacity in_attendance_capacity";
			}
		}

		@media (max-width: 560px) {
			#graph-wrapper {
				grid-template-columns: 100%;
				grid-template-rows: 1fr;
				grid-template-areas:
					"capacity"
					"schedulings_volume"
					"waiting_time"
					"waiting_capacity"
					"no_show"
					"production"
					"in_attendance_capacity";
			}
		}
	}
</style>